(function () {
  // Pointer devices switch on hover; touch and keyboard go through the trigger.
  const HOVER_QUERY = "(min-width: 750px) and (hover: hover)";
  const FALLBACK_SWITCH = 500;

  class StoryTimeline {
    constructor(el) {
      this.el = el;
      this.items = Array.from(el.querySelectorAll(".story-timeline__item"));
      this.current = -1;
      this.pointerX = null;
      this.pointerY = null;
      this.settling = false;
      this.movedWhileSettling = false;

      if (!this.items.length) return;

      this.switchMs = this._readSwitchDuration();
      this.hoverQuery = window.matchMedia(HOVER_QUERY);
      this.eventController = new AbortController();
      const signal = this.eventController.signal;

      this.items.forEach((item, index) => {
        const trigger = item.querySelector(".story-timeline__item-trigger");
        if (!trigger) return;

        trigger.addEventListener("click", () => this._activate(index), {
          signal,
        });
        trigger.addEventListener("focus", () => this._activate(index), {
          signal,
        });
      });

      // The rows resize under a still cursor, so per-item enter events fire for
      // whatever slides past it. Resolving the row from the pointer position, and
      // only after the pointer really moved, keeps a sweep across a seam stable.
      el.addEventListener(
        "pointermove",
        (event) => this._onPointerMove(event),
        {
          signal,
        }
      );
      el.addEventListener("pointerleave", () => this._forgetPointer(), {
        signal,
      });

      el.classList.add("story-timeline__list--ready");
      this._activate(0);
    }

    destroy() {
      clearTimeout(this.settleTimer);
      if (this.eventController) this.eventController.abort();
    }

    _readSwitchDuration() {
      const raw = getComputedStyle(this.el)
        .getPropertyValue("--story-timeline-switch")
        .trim();
      const value = parseFloat(raw);

      if (!value) return FALLBACK_SWITCH;

      return raw.endsWith("ms") ? value : value * 1000;
    }

    _forgetPointer() {
      this.pointerX = null;
      this.pointerY = null;
    }

    _onPointerMove(event) {
      if (event.pointerType && event.pointerType !== "mouse") return;
      if (!this.hoverQuery.matches) return;
      // A layout shift under a still cursor repeats the last coordinates.
      if (event.clientX === this.pointerX && event.clientY === this.pointerY) {
        return;
      }

      this.pointerX = event.clientX;
      this.pointerY = event.clientY;

      if (this.settling) {
        this.movedWhileSettling = true;
        return;
      }

      this._activateFromPointer();
    }

    _activateFromPointer() {
      if (this.pointerX === null || this.pointerY === null) return;

      const target = document.elementFromPoint(this.pointerX, this.pointerY);
      if (!target) return;

      const item = target.closest(".story-timeline__item");
      if (!item || !this.el.contains(item)) return;

      this._activate(this.items.indexOf(item));
    }

    _activate(index) {
      if (index < 0 || index === this.current) return;
      this.current = index;

      this.items.forEach((item, i) => {
        const isActive = i === index;

        item.classList.toggle("is-active", isActive);
        item.classList.toggle("is-past", i < index);

        const trigger = item.querySelector(".story-timeline__item-trigger");
        if (trigger) {
          trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
        }
      });

      this._holdUntilSettled();
    }

    // Hover is ignored while the rows travel, then re-read once — but only if the
    // pointer moved meanwhile, so a settled cursor never triggers another switch.
    _holdUntilSettled() {
      this.settling = true;
      this.movedWhileSettling = false;

      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        this.settling = false;
        if (this.movedWhileSettling) this._activateFromPointer();
      }, this.switchMs);
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains("story-timeline-section")) {
      return;
    }

    const root = section.querySelector(".story-timeline__list");
    if (!root) return;

    if (root._storyTimelineInstance) {
      root._storyTimelineInstance.destroy();
      root._storyTimelineInstance = null;
    }

    root._storyTimelineInstance = new StoryTimeline(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeStoryTimelineEvents) {
    window.__themeStoryTimelineEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      event.target
        .querySelectorAll(".story-timeline__list")
        .forEach(function (el) {
          if (!el._storyTimelineInstance) return;

          el._storyTimelineInstance.destroy();
          el._storyTimelineInstance = null;
        });
    });
  }
})();
